import { useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import Head from 'next/head';

export default function APIReference() {
  const { isAuthenticated, loading } = useAuth();
  const router = useRouter();
  const { theme } = useTheme();
  const [iframeKey, setIframeKey] = useState(Date.now());
  const [isLoading, setIsLoading] = useState(true);
  const [mounted, setMounted] = useState(false);

  // Redirect to login if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      router.push({
        pathname: '/login',
        query: { redirect: router.asPath }
      });
    }
  }, [isAuthenticated, loading, router]);

  // Prevent hydration mismatch by only rendering iframe after mount
  useEffect(() => {
    setMounted(true);
  }, []);

  // Don't render the page if not authenticated or still loading
  if (!isAuthenticated || loading) {
    return (
      <div style={{
        width: '100%',
        height: '100%',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: theme === 'dark' ? '#111827' : '#ffffff'
      }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Generate iframe source with theme parameter
  const iframeSrc = `/api-reference-frame?theme=${theme}`;

  // Use appropriate colors based on theme
  const backgroundColor = theme === 'dark' ? '#111827' : '#ffffff';
  const textColor = theme === 'dark' ? '#9ca3af' : '#6b7280';

  return (
    <>
      <Head>
        <title>API Reference - CreoleCentric</title>
      </Head>

      <div
        style={{
          width: '100%',
          height: '100%',
          minHeight: '100vh',
          backgroundColor: backgroundColor,
          isolation: 'isolate',
          position: 'relative'
        }}
      >
        {/* Loading indicator - show until iframe loads */}
        {(!mounted || isLoading) && (
          <div
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              color: textColor,
              fontSize: '16px',
              fontWeight: 500
            }}
          >
            Loading API Reference...
          </div>
        )}

        {/* Only render iframe after component is mounted (client-side only) */}
        {mounted && (
          <iframe
            key={iframeKey}
            src={iframeSrc}
            onLoad={() => setIsLoading(false)}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              display: 'block',
              opacity: isLoading ? 0 : 1,
              transition: 'opacity 0.3s ease-in-out'
            }}
            title="API Reference Documentation"
          />
        )}
      </div>
    </>
  );
}
